import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Globe, Save, RefreshCw, Mail, CheckCircle, XCircle, ExternalLink } from 'lucide-react';

export default function AdminPlatformSettings({
  platformDomain,
  setPlatformDomain,
  onSave,
  saving,
  // Google integration props
  googleConnected,
  googleEmail,
  onConnectGoogle,
  onDisconnectGoogle,
  connectingGoogle,
}) {
  return (
    <Card className="rounded-sm bg-slate-800 border-slate-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Globe className="h-5 w-5 text-blue-400" />
          Platform Settings
        </CardTitle>
        <CardDescription className="text-slate-400">
          Configure global settings for the platform
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Production Domain */}
        <div>
          <Label className="text-slate-300 mb-2 block">Production Domain</Label>
          <p className="text-sm text-slate-400 mb-3">
            This domain is used for lead capture form URLs. Make sure it matches your deployed domain.
          </p>
          <div className="flex gap-2">
            <Input
              value={platformDomain}
              onChange={(e) => setPlatformDomain(e.target.value)}
              placeholder="https://corbel.work"
              className="rounded-sm bg-slate-700 border-slate-600 text-white"
            />
            <Button
              onClick={onSave}
              disabled={saving}
              className="rounded-sm bg-blue-600 hover:bg-blue-700 shrink-0"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Google Email Integration */}
        <div className="border-t border-slate-700 pt-6">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-5 w-5 text-red-400" />
            <Label className="text-slate-300 text-base font-medium">Email Notifications (Gmail)</Label>
            {googleConnected ? (
              <Badge className="bg-green-600 ml-2">Connected</Badge>
            ) : (
              <Badge className="bg-slate-600 ml-2">Not Connected</Badge>
            )}
          </div>
          <p className="text-sm text-slate-400 mb-4">
            Connect a Google account to send email notifications for bug reports and contact form submissions.
          </p>
          
          {googleConnected ? (
            <div className="p-4 bg-slate-700/50 rounded-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-slate-200 font-medium">Connected Account</p>
                    <p className="text-sm text-slate-400">{googleEmail}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDisconnectGoogle}
                  className="rounded-sm border-red-600/50 text-red-400 hover:bg-red-600/10"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Disconnect
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Bug reports and contact messages will be sent from this email address.
              </p>
            </div>
          ) : (
            <div className="p-4 bg-slate-700/50 rounded-sm">
              <div className="flex items-center gap-3 mb-3">
                <XCircle className="h-5 w-5 text-slate-500" />
                <p className="text-slate-400">No Google account connected</p>
              </div>
              <Button
                onClick={onConnectGoogle}
                disabled={connectingGoogle}
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
              >
                {connectingGoogle ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Connect Google Account
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500 mt-3">
                This will redirect you to Google to authorize email sending.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
